import React, { useCallback, useMemo } from 'react';
import Service from '@/pages/dashboard/components/PageContainer/Service';
import { stringToFunction } from '@/pages/dashboard/utils/stringToJson';
import type { IComponentServiceProps } from '@/pages/dashboard/components/PageContainer/type';
import {
  applySpecialDealsFlag,
  mergeSpecialDealsTrackData,
} from '@home/pages/studio/home/utils/specialDealsRequest';

export interface ISpecialDealsServiceProps extends IComponentServiceProps {
  /** 为 true 且 data.specialDealsEnabled 为 true 时追加 logical_flag */
  appendSpecialDealsFlag?: boolean;
}

/**
 * 不改动 dashboard Service：在 beforeRequest 之后追加 Special Deals logical_flag，
 * 并自动把 specialDealsEnabled 并入 trackData。
 */
const SpecialDealsService: React.FC<ISpecialDealsServiceProps> = props => {
  const {
    appendSpecialDealsFlag,
    beforeRequest,
    trackData,
    ...rest
  } = props;

  const effectiveTrackData = useMemo(
    () => mergeSpecialDealsTrackData(trackData, appendSpecialDealsFlag),
    [trackData, appendSpecialDealsFlag],
  );

  const wrappedBeforeRequest = useCallback(
    (reqData: Record<string, unknown>) => {
      let params: unknown = reqData;
      if (beforeRequest) {
        if (typeof beforeRequest === 'string') {
          params = stringToFunction(beforeRequest)(reqData);
        } else if (typeof beforeRequest === 'function') {
          params = beforeRequest(reqData);
        }
      }
      return applySpecialDealsFlag(
        params,
        reqData as { specialDealsEnabled?: boolean },
        appendSpecialDealsFlag,
      );
    },
    [beforeRequest, appendSpecialDealsFlag],
  );

  return (
    <Service
      {...rest}
      trackData={effectiveTrackData}
      beforeRequest={wrappedBeforeRequest}
    />
  );
};

export default SpecialDealsService;
